import {ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnInit, AfterViewInit, OnDestroy, Output, SimpleChanges, QueryList, ViewChild, ViewChildren, ElementRef} from '@angular/core';
import {PageImages} from "@models/document-file";
import {Box} from "@components/main/review/human-validation/models/box";
import {BoxDrawerComponent} from "@components/main/review/human-validation/shared/box-drawer/box-drawer.component";
import {DocumentFileService} from "@services/document-file.service";
import {DocumentTextHighlight} from "@components/shared/document-viewer/models/document-text-highlight";
import {PdfSearchComponent} from "@components/shared/document-viewer/pdf-toolbar/pdf-search/pdf-search.component";
import {Subscription} from "rxjs";
import {finalize} from "rxjs/operators";

interface SearchMatch {
  ulc: { x: number, y: number };
  urc: { x: number, y: number };
  llc: { x: number, y: number };
  lrc: { x: number, y: number };
  rawText: string;
  fontSize: number;
  pageSize: { width: number, height: number };
  pageNumber: number;
}

const zoomIncrement = 0.2;
const minimumImgScale = 0.3;

@Component({
    selector: 'app-table-drawing-page',
    templateUrl: './table-drawing-page.component.html',
    styleUrls: ['./table-drawing-page.component.scss'],
    standalone: false
})
export class TableDrawingPageComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() allowOnlyOneBox: boolean
  @Input() pageImages: Array<PageImages>;

  // the currentBoxesByPage structure will always include an entry for each image in the document, therefore NOT always
  // matching the length of the possibly filtered pageImages array.
  @Input() currentBoxesByPage?: Array<Array<Box>> = [];
  @Input() showBoxDrawing?: boolean = true;
  @Input() initialPageIndex?: number;
  @Input() documentFileId?: number;
  @Output() showBoxDrawingChange = new EventEmitter<boolean>();
  @Output() boxesChanged = new EventEmitter<{ boxes: Array<Box>, activePageIndex: number }>();

  // we need to maintain two separate indexes to identify the active page because the pageImages array can be filtered
  // to include only visible images in certain scenarios:
  activePageTrueIndex: number = 0; // maintains the true page number (position in the complete list of images)
  activePageIndexInCurrentPageImagesArray: number = 0; // index in the current (filtered) page images array
  boxDrawingWidth: any;
  imgScale = 1;
  MATH = Math;
  @ViewChildren('thumbnailElement') thumbnailElements: QueryList<ElementRef>;
  @ViewChild(PdfSearchComponent) private pdfSearchComponent?: PdfSearchComponent;
  @ViewChild(BoxDrawerComponent) private boxDrawerComponent?: BoxDrawerComponent;

  searchText: string = '';
  searchPerformed: boolean = false;
  searchLoading: boolean = false;
  searchError: string = '';
  filteredPageImages: Array<PageImages>;
  private matchingPageNumbers: Set<number> = new Set();
  private searchSub: Subscription;

  searchHighlights: DocumentTextHighlight[] = [];
  activeHighlightIndex: number = -1;
  activeHighlightId: string = null;
  private activeHighlightPageIndex: number = -1;
  private highlightsPerPage: Map<number, DocumentTextHighlight[]> = new Map();
  private pendingHighlightScroll: { forceCenter: boolean } | null = null;
  isViewerImageLoaded: boolean = true;
  private _imageLoadGeneration = 0;
  displayHighlightsForCurrentPage: Array<{id: string, left: number, top: number, width: number, height: number}> = [];

  get searchEnabled(): boolean {
    return this.documentFileId != null;
  }

  get activePageInFullArray(): PageImages | undefined {
    return this.pageImages?.find(pi => pi.pageIndex === this.activePageTrueIndex);
  }

  get totalMatches(): number {
    return this.searchHighlights?.length || 0;
  }

  get currentMatchNumber(): number {
    return this.activeHighlightIndex >= 0 ? this.activeHighlightIndex + 1 : 0;
  }

  constructor(
    private _documentFileService: DocumentFileService,
    private _elementRef: ElementRef,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.setBoxDrawingWidth();
    if (this.pageImages) {
      this.filteredPageImages = this.pageImages;
      if (this.initialPageIndex !== undefined && this.initialPageIndex !== null) {
        this.activatePage(this.initialPageIndex);
      } else {
        this.activatePage(isNaN(this.pageImages[0]?.pageIndex) ? this.pageImages[0].pageIndex : 0)
      }
    }
  }

  ngAfterViewInit() {
    if (this.initialPageIndex !== undefined && this.initialPageIndex !== null) {
      setTimeout(() => {
        this.scrollToPage(this.initialPageIndex);
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('pageImages')) {
      if (changes.pageImages.currentValue) {
        if (!this.searchPerformed) {
          this.filteredPageImages = changes.pageImages.currentValue;
        }
      }
      if (!changes.pageImages.firstChange && changes.pageImages.currentValue?.length !== changes.pageImages.previousValue?.length) {
        this.setActivePageIndexBasedOnTrueIndex();
      }
    }
  }

  ngOnDestroy() {
    this.searchSub?.unsubscribe();
  }

  setBoxDrawingWidth() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    this.boxDrawingWidth = (windowWidth / 2) - 45 // offset for column padding
  }

  activatePage(pageIndex) {
    const pageChanged = this.activePageTrueIndex !== pageIndex;
    this.activePageTrueIndex = pageIndex;
    this.setActivePageIndexBasedOnTrueIndex();
    this.showBoxDrawing = true;
    this.showBoxDrawingChange.emit(true);
    if (pageChanged) {
      this.isViewerImageLoaded = false;
      this._imageLoadGeneration++;
      this.updateDisplayHighlights();
    }
  }

  setActivePageIndexBasedOnTrueIndex() {
    const displayPages = this.filteredPageImages || this.pageImages;
    const trueIndex = displayPages.findIndex(pi => pi.pageIndex == this.activePageTrueIndex);
    if (trueIndex > -1) {
      this.activePageIndexInCurrentPageImagesArray = trueIndex;
    } else {
      this.activePageTrueIndex = displayPages[0]?.pageIndex ?? 0;
      this.activePageIndexInCurrentPageImagesArray = 0;
    }
  }

  zoomIn() {
    this.imgScale += zoomIncrement;
    this.onZoomChanged();
  }

  zoomOut() {
    if (this.imgScale > minimumImgScale) {
      this.imgScale -= zoomIncrement;
      this.onZoomChanged();
    }
  }

  private onZoomChanged(): void {
    if (this.searchPerformed) {
      this.isViewerImageLoaded = false;
      this.displayHighlightsForCurrentPage = [];
    }
  }

  trackByFn(_: number, item: PageImages): number {
    return item.pageIndex
  }

  scrollToPage(pageIndex: number) {
    if (!this.thumbnailElements || !this.thumbnailElements.length) {
      return;
    }
    
    const displayPages = this.filteredPageImages || this.pageImages;
    const thumbnailArray = this.thumbnailElements.toArray();
    const targetThumbnail = thumbnailArray.find((element, index) => {
      return displayPages[index]?.pageIndex === pageIndex;
    });

    if (targetThumbnail?.nativeElement) {
      targetThumbnail.nativeElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }
  }

  onSearchTextChange(value: string): void {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.searchText = value;
    this.resetSearchState(true);
  }

  onSearchSubmit(): void {
    if (this.searchLoading || !this.searchEnabled) {
      return;
    }

    const trimmed = (this.searchText || '').trim();
    if (!trimmed) {
      this.clearSearch();
      return;
    }

    this.resetSearchState(true);
    this.searchError = '';
    this.searchLoading = true;
    this.searchSub?.unsubscribe();

    this.searchSub = this._documentFileService.searchPageText(this.documentFileId, trimmed)
      .pipe(finalize(() => { this.searchLoading = false; }))
      .subscribe({
        next: (resp) => this.handleSearchResponse(resp),
        error: () => {
          this.searchError = 'Unable to search the document. Please try again.';
          this.searchPerformed = false;
          this.matchingPageNumbers.clear();
          this.filteredPageImages = this.pageImages;
          this.searchHighlights = [];
          this.activeHighlightIndex = -1;
          this.activeHighlightId = null;
          this.highlightsPerPage = new Map();
        }
      });
  }

  private handleSearchResponse(resp: any): void {
    const responseObjects = resp?.response?.objects || [];
    this.matchingPageNumbers = new Set<number>();

    const allMatches: SearchMatch[] = [];
    responseObjects.forEach(obj => {
      if (obj?.matches?.length) {
        obj.matches.forEach(match => {
          if (match.pageNumber != null) {
            this.matchingPageNumbers.add(match.pageNumber);
          }
          allMatches.push(match);
        });
      }
    });

    this.searchPerformed = true;

    if (this.matchingPageNumbers.size === 0) {
      this.filteredPageImages = [];
      this.applySearchHighlights([]);
      return;
    }

    this.filteredPageImages = this.pageImages.filter(
      page => this.matchingPageNumbers.has(page.pageIndex + 1)
    );

    this.applySearchHighlights(this.buildHighlightsFromMatches(allMatches));

    if (this.filteredPageImages.length > 0 && this.searchHighlights.length === 0) {
      this.activatePage(this.filteredPageImages[0].pageIndex);
      setTimeout(() => this.scrollToPage(this.filteredPageImages[0].pageIndex), 100);
    }
  }

  private buildHighlightsFromMatches(matches: SearchMatch[]): DocumentTextHighlight[] {
    if (!matches?.length) {
      return [];
    }
    return matches.map((match, idx) => ({
      id: `highlight-${idx}`,
      pageNumber: match.pageNumber,
      pageSize: match.pageSize,
      x1: match.ulc.x,
      y1: match.ulc.y,
      x2: match.lrc.x,
      y2: match.llc.y
    }));
  }

  private applySearchHighlights(highlights: DocumentTextHighlight[]): void {
    this.searchPerformed = true;
    if (!highlights.length) {
      this.searchHighlights = [];
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      this.highlightsPerPage = new Map();
      return;
    }

    this.searchHighlights = highlights;
    this.buildHighlightPageCache();
    this.activeHighlightIndex = 0;
    this.activeHighlightId = this.searchHighlights[0].id;
    this.updateActiveHighlightPageIndex();
    this.updateDisplayHighlights();
    this.searchError = '';
    this.scrollToActiveHighlight(true);
    this._cdr.detectChanges();
  }

  private buildHighlightPageCache(): void {
    this.highlightsPerPage = new Map();
    for (const h of this.searchHighlights) {
      if (!this.highlightsPerPage.has(h.pageNumber)) {
        this.highlightsPerPage.set(h.pageNumber, []);
      }
      this.highlightsPerPage.get(h.pageNumber).push(h);
    }
  }

  private updateActiveHighlightPageIndex(): void {
    if (this.activeHighlightIndex >= 0 && this.activeHighlightIndex < this.searchHighlights.length) {
      this.activeHighlightPageIndex = this.searchHighlights[this.activeHighlightIndex].pageNumber - 1;
    } else {
      this.activeHighlightPageIndex = -1;
    }
  }

  getHighlightsForCurrentPage(): DocumentTextHighlight[] {
    return this.highlightsPerPage.get(this.activePageTrueIndex + 1) || [];
  }

  updateDisplayHighlights(): void {
    const highlights = this.getHighlightsForCurrentPage();
    if (!highlights.length) {
      this.displayHighlightsForCurrentPage = [];
      return;
    }

    const renderedWidth = this.boxDrawerComponent?.imageWidth || (this.boxDrawingWidth * this.imgScale);
    const renderedHeight = this.boxDrawerComponent?.imageHeight;
    this.displayHighlightsForCurrentPage = highlights.map(h => {
      const pageWidth = h.pageSize?.width || 1;
      const pageHeight = h.pageSize?.height || 1;
      const scaleX = renderedWidth / pageWidth;
      const scaleY = renderedHeight ? (renderedHeight / pageHeight) : scaleX;
      return {
        id: h.id,
        left: h.x1 * scaleX,
        top: h.y1 * scaleY,
        width: (h.x2 - h.x1) * scaleX,
        height: (h.y2 - h.y1) * scaleY
      };
    });
  }

  private resetSearchState(keepText: boolean): void {
    if (!keepText) {
      this.searchText = '';
    }
    this.searchPerformed = false;
    this.searchHighlights = [];
    this.activeHighlightIndex = -1;
    this.activeHighlightId = null;
    this.activeHighlightPageIndex = -1;
    this.highlightsPerPage = new Map();
    this.displayHighlightsForCurrentPage = [];
    this.searchError = '';
    this.matchingPageNumbers = new Set();
    this.filteredPageImages = this.pageImages;
  }

  clearSearch(): void {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.pendingHighlightScroll = null;
    this.pdfSearchComponent?.resetSearchSubject();
    this.resetSearchState(false);
  }

  onSearchClear(): void {
    this.clearSearch();
  }

  goToNextMatch(): void {
    if (!this.searchHighlights.length) return;
    this.setActiveHighlightIndex(this.activeHighlightIndex + 1);
  }

  goToPreviousMatch(): void {
    if (!this.searchHighlights.length) return;
    this.setActiveHighlightIndex(this.activeHighlightIndex - 1);
  }

  private setActiveHighlightIndex(nextIndex: number): void {
    if (!this.searchHighlights.length) {
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      return;
    }

    const len = this.searchHighlights.length;
    const normalizedIndex = ((nextIndex % len) + len) % len;
    this.activeHighlightIndex = normalizedIndex;
    this.activeHighlightId = this.searchHighlights[normalizedIndex].id;
    this.updateActiveHighlightPageIndex();
    this.scrollToActiveHighlight(false);
    this._cdr.detectChanges();
  }

  onBoxDrawerImageLoaded(): void {
    if (this._imageLoadGeneration > 0) {
      this._imageLoadGeneration--;
    }
    if (this._imageLoadGeneration > 0) return;
    this.isViewerImageLoaded = true;
    this.updateDisplayHighlights();
    if (this.pendingHighlightScroll) {
      const { forceCenter } = this.pendingHighlightScroll;
      this.pendingHighlightScroll = null;
      this._cdr.detectChanges();
      this.scrollToHighlightElement(forceCenter);
    }
  }

  private scrollToActiveHighlight(forceCenter: boolean): void {
    if (this.activeHighlightId === null) return;
    this.pendingHighlightScroll = null;

    const pageChanged = this.activeHighlightPageIndex >= 0
      && this.activePageTrueIndex !== this.activeHighlightPageIndex;

    if (pageChanged) {
      this.pendingHighlightScroll = { forceCenter };
      this.activatePage(this.activeHighlightPageIndex);
      this._cdr.detectChanges();
      setTimeout(() => this.scrollToPage(this.activeHighlightPageIndex));
    } else if (this.boxDrawerComponent?.loadedImage) {
      this.scrollToHighlightElement(forceCenter);
    } else {
      this.isViewerImageLoaded = false;
      this.pendingHighlightScroll = { forceCenter };
    }
  }

  private scrollToHighlightElement(forceCenter: boolean, retriesLeft: number = 3): void {
    const delay = retriesLeft >= 3 ? 0 : 50;
    setTimeout(() => {
      if (this.activeHighlightId === null) return;
      const highlightEl = this._elementRef.nativeElement.querySelector(
        `[data-highlight-id="${this.activeHighlightId}"]`
      );
      if (!highlightEl) {
        if (retriesLeft > 0) {
          this.scrollToHighlightElement(forceCenter, retriesLeft - 1);
        }
        return;
      }

      const container = this._elementRef.nativeElement.querySelector('#box-drawer-scroll-container');
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const highlightRect = highlightEl.getBoundingClientRect();
      const offsetTop = highlightRect.top - containerRect.top + container.scrollTop;
      const targetTop = offsetTop - (containerRect.height / 2) + (highlightRect.height / 2);
      if (forceCenter) {
        container.scrollTo({ top: targetTop, behavior: 'smooth' });
      } else {
        const visibleTop = container.scrollTop;
        const visibleBottom = visibleTop + containerRect.height;
        if (offsetTop < visibleTop || offsetTop + highlightRect.height > visibleBottom) {
          container.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
      }
    }, delay);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.searchEnabled || !this.searchPerformed || this.totalMatches === 0) return;
    const target = event.target as HTMLElement;
    if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.goToNextMatch();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.goToPreviousMatch();
    }
  }
}
